import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { Action, Location, Update } from 'history';
import { getItemFromSessionStorage, setItemInSessionStorage } from 'mini-session-storage';
import { trace } from 'owa-trace';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useSingleton } from 'mini-hooks/lib/useSingleton';

const NAVIGATION_KEY = 'miniNavigationState';

interface MiniNavigationEntry {
    index: number;
    location: Location;
}

interface MiniNavigationState {
    currentEntry: MiniNavigationEntry;
    entries: MiniNavigationEntry[];
}

const MiniNavigationContext = createContext<MiniNavigationState>(null);

const useMiniNavigation = (): MiniNavigationState => {
    const miniNavigation = useContext(MiniNavigationContext);
    if (!miniNavigation) {
        throw new Error('invoke useMiniNavigation inside MiniNavigationContextProvider');
    }

    return miniNavigation;
};

interface MiniNavigationInternalState {
    currentEntryIndex: number;
    entries: MiniNavigationEntry[];
    entryKeyToIndexMap: Record<string, number>;
}

const navigationContextReducer = (state: MiniNavigationInternalState, update: Update) => {
    let currentEntryIndex = state.currentEntryIndex;
    const entries = [...state.entries];
    const entryKeyToIndexMap = { ...state.entryKeyToIndexMap };

    const { action, location } = update;

    if (action === Action.Pop) {
        if (entryKeyToIndexMap[location.key] !== undefined) {
            currentEntryIndex = entryKeyToIndexMap[location.key];
            entries[currentEntryIndex] = {
                index: currentEntryIndex,
                location: location,
            };
        } else {
            trace.warn(`(${location.pathname}) is not managed by Mini's react router`);
        }
    } else if (action === Action.Push || action === Action.Replace) {
        currentEntryIndex = action === Action.Push ? currentEntryIndex + 1 : currentEntryIndex;
        const deletedEntries = entries.splice(currentEntryIndex);
        deletedEntries.forEach(entry => delete entryKeyToIndexMap[entry.location.key]);
        entries.push({
            index: currentEntryIndex,
            location: location,
        });
        entryKeyToIndexMap[location.key] = currentEntryIndex;
    }

    return {
        currentEntryIndex,
        entries,
        entryKeyToIndexMap,
    };
};

const getInitialMiniNavigationState = (location: Location): MiniNavigationInternalState => {
    const savedNavigationState: MiniNavigationInternalState = getItemFromSessionStorage(
        NAVIGATION_KEY
    );

    if (savedNavigationState) {
        const { entries, currentEntryIndex } = savedNavigationState;
        const currentEntry = entries?.[currentEntryIndex];
        if (
            currentEntry?.location?.key === location.key &&
            currentEntry?.location?.pathname === location.pathname
        ) {
            return savedNavigationState;
        }
    }

    let initNavigationState = {
        currentEntryIndex: -1,
        entries: [],
        entryKeyToIndexMap: {},
    };

    // Push current location since there is no valid saved state
    initNavigationState = navigationContextReducer(initNavigationState, {
        action: Action.Push,
        location,
    });

    return initNavigationState;
};

const MiniNavigationContextProvider: React.FC<{}> = ({ children }) => {
    // This is to make sure there is only one instance of MiniNavigationContextProvider used at any point in time
    useSingleton('MiniNavigationContextProvider');

    const location = useLocation();
    const navigationType = useNavigationType();

    const [navigationInternalState, navigationInternalStateUpdater] = useReducer(
        navigationContextReducer,
        location,
        getInitialMiniNavigationState
    );

    useEffect(() => {
        navigationInternalState.entries.length > 0 &&
            setItemInSessionStorage(NAVIGATION_KEY, navigationInternalState);
    }, [navigationInternalState]);

    useEffect(() => {
        navigationInternalStateUpdater({ action: navigationType, location });
    }, [location, navigationType]);

    const navigationState: MiniNavigationState = useMemo(
        () => ({
            currentEntry:
                navigationInternalState.entries[navigationInternalState.currentEntryIndex],
            entries: navigationInternalState.entries,
        }),
        [navigationInternalState]
    );

    return (
        <MiniNavigationContext.Provider value={navigationState}>
            {children}
        </MiniNavigationContext.Provider>
    );
};

export {
    MiniNavigationEntry,
    MiniNavigationState,
    MiniNavigationContextProvider,
    useMiniNavigation,
};
